import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import "./Affiliate.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useEffect, useState } from "react";
import { GetService, PostService } from "../../../Services/ConstantService";
import { API_URL } from "../../../Services/ApiService";
import { toastEmmit } from "../../../Helper/Toastr";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: "",

  affiliateType: "",
  businessName: "",
  website: "",

  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",

  promotionMethods: [],

  socialLinks: {
    instagram: "",
    facebook: "",
    youtube: "",
    tiktok: "",
    pinterest: "",
    linkedin: "",
  },

  audienceSize: "",
  reason: "",

  paymentMethod: "",

  paypalEmail: "",

  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  routingNumber: "",
  swiftCode: "",
  iban: "",

  termsAccepted: false,
  informationConfirmed: false,
};

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),

  lastName: Yup.string()
    .trim()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),

  email: Yup.string()
    .trim()
    .email("Please enter a valid email address")
    .required("Email address is required"),

  phone: Yup.string()
    .required("Mobile number is required")
    .test("valid-phone", "Please enter a valid mobile number", (value) => {
      if (!value) return false;

      const digits = value.replace(/\D/g, "");

      return digits.length >= 8 && digits.length <= 15;
    }),

  dateOfBirth: Yup.string().nullable(),

  affiliateType: Yup.string().required("Please select affiliate type"),

  businessName: Yup.string().when("affiliateType", {
    is: "Business",
    then: (schema) => schema.trim().required("Business name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  website: Yup.string()
    .trim()
    .url("Please enter a valid website URL")
    .nullable(),

  streetAddress: Yup.string().trim().required("Street address is required"),

  city: Yup.string().trim().required("City is required"),

  state: Yup.string().trim().required("State / Province is required"),

  zipCode: Yup.string().trim().required("ZIP / Postal Code is required"),

  country: Yup.string().trim().required("Country is required"),

  promotionMethods: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one promotion method"),

  audienceSize: Yup.string().nullable(),

  reason: Yup.string()
    .trim()
    .required("Please tell us why you want to join")
    .min(20, "Please provide at least 20 characters"),

  paymentMethod: Yup.string().required("Please select a payment method"),

  paypalEmail: Yup.string().when("paymentMethod", {
    is: "paypal",
    then: (schema) =>
      schema
        .email("Please enter a valid PayPal email")
        .required("PayPal email is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  accountHolderName: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) => schema.required("Account holder name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  bankName: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) => schema.required("Bank name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  accountNumber: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) =>
      schema
        .required("Account number is required")
        .min(4, "Please enter a valid account number"),
    otherwise: (schema) => schema.notRequired(),
  }),

  routingNumber: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) =>
      schema
        .required("Routing number is required")
        .matches(/^[0-9]{9}$/, "Routing number must contain 9 digits"),
    otherwise: (schema) => schema.notRequired(),
  }),

  swiftCode: Yup.string().nullable(),

  iban: Yup.string().nullable(),

  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must accept the Affiliate Program Terms & Conditions",
  ),

  informationConfirmed: Yup.boolean().oneOf(
    [true],
    "Please confirm that the information is accurate",
  ),
});

const AffiliateSignup = () => {
  const [CountryList, setCountryList] = useState([]);
  const [StateList, setStateList] = useState([]);
  const [loading, setloading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    GetCountryList();
  }, []);

  const GetCountryList = () => {
    setloading(true);

    GetService(API_URL.GET_COUNTRY_LIST)
      .then((res) => {
        if (res?.data?.status) {
          setCountryList(res?.data?.data || []);
        } else {
          setCountryList([]);
        }
      })
      .catch((error) => {
        toastEmmit("error", error?.response?.data?.message);
        setCountryList([]);
      })
      .finally(() => {
        setloading(false);
      });
  };

  const GetStateList = (cid) => {
    setloading(true);

    GetService(`${API_URL.GET_STATE_LIST}?country_id=${cid}`)
      .then((res) => {
        if (res?.data?.status) {
          setStateList(res?.data?.data || []);
        } else {
          setStateList([]);
        }
      })
      .catch((error) => {
        toastEmmit("error", error?.response?.data?.message);
        setStateList([]);
      })
      .finally(() => {
        setloading(false);
      });
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    console.log(values);
    const selectedCountry = CountryList.find(
      (c) => c.country_id === Number(values.country),
    );

    const selectedState = StateList.find(
      (c) => c.state_id === Number(values.state),
    );
    console.log(selectedCountry, selectedState);
    const payload = {
      ...values,
      country: selectedCountry?.country_name || "",
      state: selectedState?.state_name || "",
    };

    try {
      const res = await PostService(API_URL.AFFILIATE_SIGNUP, payload);

      console.log(res);

      if (res?.data?.status) {
        toastEmmit(
          "success",
          "Your request has been submitted. We will get back to you soon.",
        );

        resetForm();
        navigate("/home");
      }
    } catch (error) {
      console.log(error);
      toastEmmit(
        "error",
        error?.response?.data?.message || "Something went wrong",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-20 pb-100 affiliate-signup-page">
      <div className="container">
        {/* Header */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-9">
            <Link
              to="/affiliateProgram"
              className="text-decoration-none d-inline-flex align-items-center mb-4"
            >
              <ArrowLeft size={18} className="me-2" />
              Back
            </Link>

            <div className="text-center row text">
              <h2 className="text-left second">Join Our Affiliate Program</h2>

              <p className="text-muted">
                Complete the form below to apply for the Printlific Affiliate
                Program. Your application will be reviewed by our team before
                your account is approved.
              </p>
            </div>
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form noValidate>
              <div className="row justify-content-center">
                <div className="col-xl-9 col-lg-10">
                  {/* Personal Information */}
                  <div className="affiliate-form-card mb-4">
                    <div className="affiliate-form-header">
                      <h4>1. Personal Information</h4>
                      <p>Tell us a little about yourself.</p>
                    </div>

                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label">
                          First Name <span className="text-danger">*</span>
                        </label>

                        <Field
                          name="firstName"
                          type="text"
                          className={`form-control ${
                            touched.firstName && errors.firstName
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter first name"
                        />

                        <ErrorMessage
                          name="firstName"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Last Name <span className="text-danger">*</span>
                        </label>

                        <Field
                          name="lastName"
                          type="text"
                          className={`form-control ${
                            touched.lastName && errors.lastName
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter last name"
                        />

                        <ErrorMessage
                          name="lastName"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Email Address <span className="text-danger">*</span>
                        </label>

                        <Field
                          name="email"
                          type="email"
                          className={`form-control ${
                            touched.email && errors.email ? "is-invalid" : ""
                          }`}
                          placeholder="Enter email address"
                        />

                        <ErrorMessage
                          name="email"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Mobile Number <span className="text-danger">*</span>
                        </label>

                        <PhoneInput
                          country="us"
                          value={values.phone}
                          onChange={(phone) => {
                            setFieldValue("phone", phone ? `+${phone}` : "");
                          }}
                          onBlur={() => {
                            setFieldTouched("phone", true);
                          }}
                          inputProps={{
                            name: "phone",
                            required: true,
                            autoComplete: "tel",
                          }}
                          enableSearch
                          countryCodeEditable={false}
                          containerClass={
                            touched.phone && errors.phone
                              ? "phone-input-error"
                              : ""
                          }
                          inputClass="form-control"
                        />

                        {touched.phone && errors.phone && (
                          <div className="text-danger small mt-1">
                            {errors.phone}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Date of Birth{" "}
                          <span className="text-muted">(Optional)</span>
                        </label>

                        <Field
                          name="dateOfBirth"
                          type="date"
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="affiliate-form-card mb-4">
                    <div className="affiliate-form-header">
                      <h4>2. Business Information</h4>
                      <p>
                        Tell us about your business or professional activity.
                      </p>
                    </div>

                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label">
                          Affiliate Type <span className="text-danger">*</span>
                        </label>

                        <Field
                          as="select"
                          name="affiliateType"
                          className={`form-select ${
                            touched.affiliateType && errors.affiliateType
                              ? "is-invalid"
                              : ""
                          }`}
                        >
                          <option value="">Select affiliate type</option>
                          <option value="Individual">Individual</option>
                          <option value="Business">Business</option>
                          <option value="Influencer">Influencer</option>
                          <option value="Photographer">Photographer</option>
                          <option value="Content Creator">
                            Content Creator
                          </option>
                          <option value="Agency">Agency</option>
                          <option value="Other">Other</option>
                        </Field>

                        <ErrorMessage
                          name="affiliateType"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Business Name{" "}
                          {values.affiliateType === "Business" ? (
                            <span className="text-danger">*</span>
                          ) : (
                            <span className="text-muted">(Optional)</span>
                          )}
                        </label>

                        <Field
                          name="businessName"
                          type="text"
                          className={`form-control ${
                            touched.businessName && errors.businessName
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter business name"
                        />

                        <ErrorMessage
                          name="businessName"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          Website <span className="text-muted">(Optional)</span>
                        </label>

                        <Field
                          name="website"
                          type="url"
                          className={`form-control ${
                            touched.website && errors.website
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="https://example.com"
                        />

                        <ErrorMessage
                          name="website"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          Street Address <span className="text-danger">*</span>
                        </label>

                        <Field
                          name="streetAddress"
                          type="text"
                          className={`form-control ${
                            touched.streetAddress && errors.streetAddress
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter street address"
                        />

                        <ErrorMessage
                          name="streetAddress"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Country <span className="text-danger">*</span>
                        </label>

                        <Field
                          as="select"
                          name="country"
                          className={`form-select ${
                            touched.country && errors.country
                              ? "is-invalid"
                              : ""
                          }`}
                          onChange={(e) => {
                            const countryId = e.target.value;

                            // Update Formik country
                            setFieldValue("country", countryId);

                            // Clear previously selected state
                            setFieldValue("state", "");

                            // Clear previous state list
                            setStateList([]);

                            // Get states for selected country
                            if (countryId) {
                              GetStateList(countryId);
                            }
                          }}
                        >
                          <option value="" hidden>
                            Select Country
                          </option>

                          {CountryList.map((country) => (
                            <option
                              key={country.country_id}
                              value={country.country_id}
                            >
                              {country.country_name}
                            </option>
                          ))}
                        </Field>

                        <ErrorMessage
                          name="country"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          State / Province{" "}
                          <span className="text-danger">*</span>
                        </label>

                        <Field
                          as="select"
                          name="state"
                          disabled={!values.country || StateList.length === 0}
                          className={`form-select ${
                            touched.state && errors.state ? "is-invalid" : ""
                          }`}
                        >
                          <option value="" hidden>
                            {!values.country
                              ? "Select Country First"
                              : StateList.length === 0
                                ? "No States Found"
                                : "Select State / Province"}
                          </option>

                          {StateList.map((state) => (
                            <option key={state.state_id} value={state.state_id}>
                              {state.state_name}
                            </option>
                          ))}
                        </Field>

                        <ErrorMessage
                          name="state"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          City <span className="text-danger">*</span>
                        </label>

                        <Field
                          name="city"
                          type="text"
                          className={`form-control ${
                            touched.city && errors.city ? "is-invalid" : ""
                          }`}
                          placeholder="Enter city"
                        />

                        <ErrorMessage
                          name="city"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          ZIP / Postal Code{" "}
                          <span className="text-danger">*</span>
                        </label>

                        <Field
                          name="zipCode"
                          type="text"
                          className={`form-control ${
                            touched.zipCode && errors.zipCode
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter ZIP / Postal code"
                        />

                        <ErrorMessage
                          name="zipCode"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Promotion */}
                  <div className="affiliate-form-card mb-4">
                    <div className="affiliate-form-header">
                      <h4>3. Promotion Information</h4>
                      <p>
                        Help us understand how you plan to promote printlific.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="form-label d-block">
                        How do you plan to promote printlific?{" "}
                        <span className="text-danger">*</span>
                      </label>

                      <div className="row g-3">
                        {[
                          ["website", "Website"],
                          ["blog", "Blog"],
                          ["instagram", "Instagram"],
                          ["facebook", "Facebook"],
                          ["youtube", "YouTube"],
                          ["tiktok", "TikTok"],
                          ["pinterest", "Pinterest"],
                          ["email", "Email Marketing"],
                          ["word_of_mouth", "Word of Mouth"],
                          ["other", "Other"],
                        ].map(([value, label]) => (
                          <div className="col-md-4 col-sm-6" key={value}>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                id={`promotion-${value}`}
                                className="form-check-input"
                                checked={values.promotionMethods.includes(
                                  value,
                                )}
                                onChange={(e) => {
                                  const current = values.promotionMethods;

                                  if (e.target.checked) {
                                    setFieldValue("promotionMethods", [
                                      ...current,
                                      value,
                                    ]);
                                  } else {
                                    setFieldValue(
                                      "promotionMethods",
                                      current.filter((item) => item !== value),
                                    );
                                  }
                                }}
                              />

                              <label
                                htmlFor={`promotion-${value}`}
                                className="form-check-label"
                              >
                                {label}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>

                      {touched.promotionMethods && errors.promotionMethods && (
                        <div className="text-danger small mt-2">
                          {errors.promotionMethods}
                        </div>
                      )}
                    </div>

                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label">Instagram Profile</label>

                        <Field
                          name="socialLinks.instagram"
                          type="url"
                          className="form-control"
                          placeholder="https://instagram.com/..."
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Facebook Profile</label>

                        <Field
                          name="socialLinks.facebook"
                          type="url"
                          className="form-control"
                          placeholder="https://facebook.com/..."
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">YouTube Channel</label>

                        <Field
                          name="socialLinks.youtube"
                          type="url"
                          className="form-control"
                          placeholder="https://youtube.com/..."
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">TikTok Profile</label>

                        <Field
                          name="socialLinks.tiktok"
                          type="url"
                          className="form-control"
                          placeholder="https://tiktok.com/@..."
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Pinterest Profile</label>

                        <Field
                          name="socialLinks.pinterest"
                          type="url"
                          className="form-control"
                          placeholder="https://pinterest.com/..."
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">LinkedIn Profile</label>

                        <Field
                          name="socialLinks.linkedin"
                          type="url"
                          className="form-control"
                          placeholder="https://linkedin.com/in/..."
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Estimated Monthly Audience
                        </label>

                        <Field
                          as="select"
                          name="audienceSize"
                          className="form-select"
                        >
                          <option value="">Select audience size</option>
                          <option value="less_than_1000">
                            Less than 1,000
                          </option>
                          <option value="1000-10,000">1,000 – 10,000</option>
                          <option value="10,000-50,000">10,000 – 50,000</option>
                          <option value="50,000-100,000">
                            50,000 – 100,000
                          </option>
                          <option value="100,000+">100,000+</option>
                        </Field>
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          Why would you like to join the Printlific Affiliate
                          Program? <span className="text-danger">*</span>
                        </label>

                        <Field
                          as="textarea"
                          name="reason"
                          rows={3}
                          className={`form-control ${
                            touched.reason && errors.reason ? "is-invalid" : ""
                          }`}
                          placeholder="Tell us about yourself and how you plan to promote printlific..."
                        />

                        <ErrorMessage
                          name="reason"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment */}
                  <div className="affiliate-form-card mb-4">
                    <div className="affiliate-form-header">
                      <h4>4. Payment Information</h4>
                      <p>
                        Select how you would like to receive your affiliate
                        commissions.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="form-label d-block">
                        Preferred Payment Method{" "}
                        <span className="text-danger">*</span>
                      </label>

                      <div className="row">
                        <div className="col-md-6">
                          <div
                            className="form-check payment-option"
                            onClick={() =>
                              setFieldValue("paymentMethod", "paypal")
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="paypal"
                              value="paypal"
                              checked={values.paymentMethod === "paypal"}
                              onChange={() =>
                                setFieldValue("paymentMethod", "paypal")
                              }
                            />

                            <label
                              className="form-check-label"
                              htmlFor="paypal"
                            >
                              PayPal
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div
                            className="form-check payment-option"
                            onClick={() =>
                              setFieldValue("paymentMethod", "bank_transfer")
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="bank_transfer"
                              value="bank_transfer"
                              checked={values.paymentMethod === "bank_transfer"}
                              onChange={() =>
                                setFieldValue("paymentMethod", "bank_transfer")
                              }
                            />

                            <label
                              className="form-check-label"
                              htmlFor="bank_transfer"
                            >
                              Bank Transfer
                            </label>
                          </div>
                        </div>
                      </div>

                      {touched.paymentMethod && errors.paymentMethod && (
                        <div className="text-danger small mt-2">
                          {errors.paymentMethod}
                        </div>
                      )}
                    </div>

                    {values.paymentMethod === "paypal" && (
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label">
                            PayPal Email <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="paypalEmail"
                            type="email"
                            className={`form-control ${
                              touched.paypalEmail && errors.paypalEmail
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter PayPal email"
                          />

                          <ErrorMessage
                            name="paypalEmail"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                    )}

                    {values.paymentMethod === "bank_transfer" && (
                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label">
                            Account Holder Name{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="accountHolderName"
                            type="text"
                            className={`form-control ${
                              touched.accountHolderName &&
                              errors.accountHolderName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter account holder name"
                          />

                          <ErrorMessage
                            name="accountHolderName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Bank Name <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="bankName"
                            type="text"
                            className={`form-control ${
                              touched.bankName && errors.bankName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter bank name"
                          />

                          <ErrorMessage
                            name="bankName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Account Number{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="accountNumber"
                            type="password"
                            className={`form-control ${
                              touched.accountNumber && errors.accountNumber
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter account number"
                          />

                          <ErrorMessage
                            name="accountNumber"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Routing Number{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="routingNumber"
                            type="text"
                            inputMode="numeric"
                            className={`form-control ${
                              touched.routingNumber && errors.routingNumber
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="9 digit routing number"
                          />

                          <ErrorMessage
                            name="routingNumber"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            SWIFT / BIC Code{" "}
                            <span className="text-muted">(Optional)</span>
                          </label>

                          <Field
                            name="swiftCode"
                            type="text"
                            className="form-control"
                            placeholder="Enter SWIFT / BIC"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            IBAN <span className="text-muted">(Optional)</span>
                          </label>

                          <Field
                            name="iban"
                            type="text"
                            className="form-control"
                            placeholder="Enter IBAN"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Agreements */}
                  <div className="affiliate-form-card mb-4">
                    <div className="affiliate-form-header">
                      <h4>5. Agreement</h4>
                    </div>

                    <div className="form-check mb-3">
                      <Field
                        type="checkbox"
                        name="termsAccepted"
                        id="termsAccepted"
                        className={`form-check-input ${
                          touched.termsAccepted && errors.termsAccepted
                            ? "is-invalid"
                            : ""
                        }`}
                      />

                      <label
                        htmlFor="termsAccepted"
                        className="form-check-label"
                      >
                        I agree to the Printlific Affiliate Program Terms &
                        Conditions.
                        <span className="text-danger"> *</span>
                      </label>

                      <ErrorMessage
                        name="termsAccepted"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>

                    <div className="form-check">
                      <Field
                        type="checkbox"
                        name="informationConfirmed"
                        id="informationConfirmed"
                        className={`form-check-input ${
                          touched.informationConfirmed &&
                          errors.informationConfirmed
                            ? "is-invalid"
                            : ""
                        }`}
                      />

                      <label
                        htmlFor="informationConfirmed"
                        className="form-check-label"
                      >
                        I confirm that the information provided in this
                        application is accurate.
                        <span className="text-danger"> *</span>
                      </label>

                      <ErrorMessage
                        name="informationConfirmed"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="text-center">
                    <button
                      type="submit"
                      className={`btnPrimary px-5 py-3 ${isSubmitting ? "disabled" : ""}`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          Submitting Application...
                          <span className="ms-2">
                            <i className="fa fa-spinner fa-spin fa-fw"></i>
                          </span>
                        </>
                      ) : (
                        "Submit Affiliate Application"
                      )}
                    </button>

                    <p className="text-muted small mt-3">
                      Your application will be reviewed by our team before your
                      affiliate account is approved.
                    </p>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </section>
  );
};

export default AffiliateSignup;
